/**
 * La sesión, en React · `BFF_BO.md` §3.3, §5.1 y §5.2
 *
 * **El arranque es siempre `POST /auth/refresh` → `GET /me`**, y no es un rodeo:
 * al recargar la página no hay token de acceso porque vive en memoria, así que la
 * única pista de que hay sesión es la cookie de refresco, que el JavaScript no
 * puede leer. Hay que preguntar.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, opcionesDeSesion } from '@/shared/api/client';
import {
  alPerderLaSesion,
  fijarToken,
  limpiarSesion,
  refrescar,
} from '@/shared/api/sesion';

export type Rol = 'owner' | 'admin' | 'coach' | 'assistant';

export interface Membresia {
  readonly academy_id: string;
  readonly academy_name: string;
  readonly academy_slug: string;
  readonly role: string;
  readonly status: string;
}

export interface Yo {
  readonly user: {
    readonly id: string;
    readonly email: string;
    readonly first_name?: string | null;
    readonly last_name?: string | null;
    readonly is_platform_staff: boolean;
    /**
     * La foto de perfil, o `null`. Solo el identificador: el fichero se pide a
     * `GET /files/{id}`, que comprueba permisos antes de firmar la URL.
     *
     * Estaba en `GET /me` desde el principio y no en este tipo, así que «Mi cuenta»
     * no podía saber si enseñar «Subir una foto» o «Cambiarla». 22-09-2026.
     */
    readonly avatar_asset_id?: string | null;
  };
  readonly memberships: readonly Membresia[];
  readonly can_open: readonly string[];
}

type Estado =
  | { readonly fase: 'arrancando' }
  | { readonly fase: 'fuera' }
  | { readonly fase: 'dentro'; readonly yo: Yo };

interface Contexto {
  readonly estado: Estado;
  /** El club elegido. En la URL, no solo en el estado: un enlace ha de llevar al mismo sitio. */
  readonly club: string | null;
  readonly elegirClub: (academyId: string) => void;
  /** Tras `POST /auth/password` o `/magic-link/verify`. */
  readonly entrar: (accessToken: string) => Promise<void>;
  readonly salir: () => Promise<void>;
}

const SesionContexto = createContext<Contexto | null>(null);

/** Las membresías que **abren algo**: `invited` o `suspended` no abren nada (§5.1). */
export const membresiasActivas = (yo: Yo): readonly Membresia[] =>
  yo.memberships.filter((m) => m.status === 'active');

const clubDeLaUrl = (): string | null =>
  new URLSearchParams(window.location.search).get('club');

export function ProveedorDeSesion({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ fase: 'arrancando' });
  const [club, setClub] = useState<string | null>(clubDeLaUrl);
  const cache = useQueryClient();

  const cargarYo = useCallback(async (): Promise<Yo | null> => {
    const { data, error } = await api.v1.me.get();
    if (error || !data) return null;
    return data as unknown as Yo;
  }, []);

  /** Primera membresía activa, para no enseñar un selector de uno (§5.2). */
  const clubPorDefecto = useCallback(
    (yo: Yo): string | null => membresiasActivas(yo)[0]?.academy_id ?? null,
    []
  );

  useEffect(() => {
    let vivo = true;

    // Quien pierda la sesión desde el cliente HTTP acaba aquí: una sola puerta
    // de salida, así que no hay dos sitios que limpien la mitad de las cosas.
    alPerderLaSesion(() => {
      if (vivo) setEstado({ fase: 'fuera' });
    });

    void (async () => {
      const renovada = await refrescar(opcionesDeSesion);
      if (!vivo) return;

      if (!renovada) {
        setEstado({ fase: 'fuera' });
        return;
      }

      const yo = await cargarYo();
      if (!vivo) return;

      if (!yo) {
        setEstado({ fase: 'fuera' });
        return;
      }

      setEstado({ fase: 'dentro', yo });
      setClub((actual) => actual ?? clubPorDefecto(yo));
    })();

    return () => {
      vivo = false;
    };
  }, [cargarYo, clubPorDefecto]);

  const entrar = useCallback(
    async (accessToken: string) => {
      fijarToken(accessToken);
      const yo = await cargarYo();

      if (!yo) {
        limpiarSesion();
        setEstado({ fase: 'fuera' });
        return;
      }

      setEstado({ fase: 'dentro', yo });
      setClub(clubDeLaUrl() ?? clubPorDefecto(yo));
    },
    [cargarYo, clubPorDefecto]
  );

  const salir = useCallback(async () => {
    // Se avisa al servidor para que invalide el refresco, pero si falla se sale
    // igual: quedarse dentro porque el `logout` dio error es lo contrario de lo
    // que pidió la persona.
    try {
      await api.v1.auth.logout.post();
    } catch {
      /* da igual */
    }
    cache.clear();
    limpiarSesion();
    setEstado({ fase: 'fuera' });
  }, [cache]);

  const elegirClub = useCallback(
    (academyId: string) => {
      if (academyId === club) return;

      // **Toda la caché fuera.** Un listado de alumnos del club anterior pintado
      // bajo el nombre del nuevo es el peor fallo posible en este producto (§10).
      cache.clear();

      const url = new URL(window.location.href);
      url.searchParams.set('club', academyId);
      window.history.replaceState(null, '', url);

      setClub(academyId);
    },
    [cache, club]
  );

  const valor = useMemo<Contexto>(
    () => ({ estado, club, elegirClub, entrar, salir }),
    [estado, club, elegirClub, entrar, salir]
  );

  return (
    <SesionContexto.Provider value={valor}>{children}</SesionContexto.Provider>
  );
}

export function useSesion(): Contexto {
  const valor = useContext(SesionContexto);
  if (!valor)
    throw new Error(
      'useSesion fuera de <ProveedorDeSesion>. Falta el proveedor.'
    );
  return valor;
}

/** Atajo para las pantallas que solo existen dentro. */
export function useYo(): Yo {
  const { estado } = useSesion();
  if (estado.fase !== 'dentro')
    throw new Error('useYo fuera de una sesión abierta.');
  return estado.yo;
}
